import {
  Box,
  Button,
  Flex,
  MenuItem,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import MuiIcon from 'components/MuiIcon';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetOneService, useGet3thPartyServices } from 'shared/api/requests/services';
import Card from 'shared/components/horizon-ui/card/Card';
import Link from 'shared/components/Link';
import Loader from 'shared/components/Loader';
import MainModelInfo from 'shared/components/MainModelInfo';
import useGetColors from 'shared/hooks/useGetColors';
import { PathsE, PathsParams } from 'shared/routes';
import ModelInfo from './ModelInfo';
import PageTitle from 'shared/views/admin/PageTitle';
import MainItemMenu from 'shared/components/MainItemMenu';
import DeleteModal from 'shared/views/admin/DeleteModal';

const Show = () => {
  const { t } = useTranslation();
  const { groupId, serviceId } = useParams<PathsParams[PathsE.SHOW_GROUP]>();
  const {
    gray100,
    gray700,
    gray800,
  } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const requestParams = {
    pathParams: { groupId, serviceId },
    params: {},
  };

  const handleDelete = () => onOpen();

  const { data: currentService } = useGetOneService({
    pathParams: { serviceId },
    params: undefined
  });
  
  const { useGetOneGroup, useToggleOneGroup, useDeleteGroup } = useGet3thPartyServices({
    domain: currentService?.domain,
    token: currentService?.token,
  });
  const { mutate: toggleMutation } = useToggleOneGroup({
    pathParams: { serviceId },
    params: undefined
  });
  const { mutate: deleteMutation } = useDeleteGroup({
    pathParams: { serviceId },
    params: undefined
  });

  const { data, isPending } = useGetOneGroup(requestParams);

  const breadcrumbs = [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
    {
      path: PathsE.GROUPS,
      text: t('services.groups'),
      pathsParams: {
        serviceId,
      },
    },
  ];

  const handleActivate = (groupId: string) => toggleMutation({ groupId, isActive: true });
  const handleDeactivate = (groupId: string) => toggleMutation({ groupId, isActive: true });

  if (isPending || !data) return <Loader />;

  return (
    <>
      <Box>
        <PageTitle
          title={data.name}
          breadcrumbs={breadcrumbs}
          backUrl={{ path: PathsE.GROUPS, pathsParams: { serviceId } }}
        />
        <Card mb="20px" borderRadius="20px" px="30px">
          <Flex justifyContent="space-between" alignItems="center" mt="-10px">
            <Text color={gray700} fontWeight="600">
              {data.name}
            </Text>
            <Flex alignItems="center">
              <Box mr="10px">
                <Link<PathsParams[PathsE.UPDATE_GROUP]>
                  path={PathsE.UPDATE_GROUP}
                  pathsParams={{ groupId, serviceId }}
                >
                  <Button
                    color={gray800}
                    bgColor={gray100}
                    borderRadius="8px"
                    leftIcon={<MuiIcon name="edit" />}
                  >
                    {t('services.editGroup')}
                  </Button>
                </Link>
              </Box>
              <MainItemMenu
                isModelView
                data={data as FixMeLater}
                handleActivate={handleActivate}
                handleDeactivate={handleDeactivate}
                viewLink={PathsE.UPDATE_GROUP}
                pathsParams={{ serviceId, groupId }}
              >
                <MenuItem
                  _hover={{
                    backgroundColor: gray100,
                  }}
                  onClick={handleDelete}
                >
                  <MuiIcon name="delete" />
                  <Text color={gray700} ml="8px" fontSize="sm">{t('services.deleteGroup')}</Text>
                </MenuItem>
              </MainItemMenu>
            </Flex>
          </Flex>
          <MainModelInfo data={data} />
          <ModelInfo data={data} />
        </Card>
      </Box>
      <DeleteModal
        title={t('services.deleteGroup')}
        text={t('general.approveTo', { phrase: t('services.deleteGroup') })}
        onClose={onClose}
        isOpen={isOpen}
        onSubmit={() => deleteMutation({ groupId })}
      />
    </>
  );
};

export default Show;
